import { create } from 'zustand';
import { getMe } from '@/services/auth';
import { queryClient } from '@/lib/queryClient';

let authRevision = 0;

// Stored office IDs can outlive a location or belong to a previous practice.
// Only restore locations returned for the current signed-in practice.
function restoreLocation(user, locations = []) {
  const key = `cervica_loc_${user?.id}`;
  const activeIds = new Set(locations.filter(location => location.is_active !== false).map(location => location.id));
  const perUser = localStorage.getItem(key);
  if (perUser && !activeIds.has(perUser)) localStorage.removeItem(key);
  const remembered = [perUser, localStorage.getItem('cervica_current_location'), user?.home_location_id]
    .find(id => id && activeIds.has(id)) || null;
  if (remembered) localStorage.setItem('cervica_current_location', remembered);
  else localStorage.removeItem('cervica_current_location');
  return remembered;
}

const useAuthStore = create((set, get) => ({
  user: null,
  tenant: null,
  locations: [],
  token: null,
  refreshToken: null,
  currentLocationId: localStorage.getItem('cervica_current_location') || null,
  isAuthenticated: false,
  isLoading: true,

  setAuth(loginResponse) {
    authRevision += 1;
    localStorage.setItem('cervica_auth_session', `${Date.now()}-${Math.random()}`);
    // New auth identity — drop every cached query so nothing from a
    // previous account can render (Codex feature audit finding 1).
    queryClient.clear();
    const { token, refreshToken, user, tenant, locations } = loginResponse;
    localStorage.setItem('cervica_token', token);
    localStorage.setItem('cervica_refresh_token', refreshToken);
    // Restore this USER's remembered office on this machine. Logout
    // used to wipe the location entirely, so every login started on
    // All Locations and note surfaces fell back to the first office —
    // Canton — regardless of where the provider works (Michael, Chris,
    // Ryan 2026-07-16). Keyed per user so shared front-desk machines
    // don't leak one person's office onto the next login.
    const remembered = restoreLocation(user, locations);
    set({
      user,
      tenant,
      locations,
      token,
      refreshToken,
      currentLocationId: remembered,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  clearAuth() {
    authRevision += 1;
    localStorage.setItem('cervica_auth_session', `${Date.now()}-${Math.random()}`);
    queryClient.clear();
    localStorage.removeItem('cervica_token');
    localStorage.removeItem('cervica_refresh_token');
    localStorage.removeItem('cervica_current_location');
    set({
      user: null,
      tenant: null,
      locations: [],
      token: null,
      refreshToken: null,
      currentLocationId: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  // Patch the current user record after a profile update so the rest of
  // the app (e.g., FormsTab provider-signature lookup) reflects changes
  // without requiring a logout/login cycle.
  setUser(user) {
    // Profile responses are partial; preserve resolved dashboard/location fields.
    // Ignore a response from an account that has since logged out or switched.
    if (user?.id && user.id === get().user?.id) set({ user: { ...get().user, ...user } });
  },

  // Only accept an authenticated save receipt for this same practice.
  acceptLocation(location) {
    const state = get();
    if (!location?.id || location.tenant_id !== state.tenant?.id || !state.isAuthenticated) return;
    const others = state.locations.filter(item => item.id !== location.id);
    const locations = location.is_active === false ? others : [...others, location].sort((a, b) => a.name.localeCompare(b.name));
    set({ locations });
    if (state.currentLocationId === location.id && location.is_active === false) get().setCurrentLocation(null);
  },

  setCurrentLocation(locationId) {
    const userId = get().user?.id;
    if (locationId) {
      localStorage.setItem('cervica_current_location', locationId);
      if (userId) localStorage.setItem(`cervica_loc_${userId}`, locationId);
    } else {
      localStorage.removeItem('cervica_current_location');
      if (userId) localStorage.removeItem(`cervica_loc_${userId}`);
    }
    set({ currentLocationId: locationId });
  },

  async initialize() {
    const revision = ++authRevision;
    const session = localStorage.getItem('cervica_auth_session');
    const token = localStorage.getItem('cervica_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }
    try {
      const { data } = await getMe();
      // A logout, new login, or newer initialization owns the state now.
      if (revision !== authRevision || session !== localStorage.getItem('cervica_auth_session')) return;
      const remembered = restoreLocation(data.user, data.locations);
      set({
        user: data.user,
        tenant: data.tenant,
        locations: data.locations,
        token,
        refreshToken: localStorage.getItem('cervica_refresh_token'),
        currentLocationId: remembered,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      if (revision !== authRevision || session !== localStorage.getItem('cervica_auth_session')) return;
      get().clearAuth();
    }
  },
}));

export default useAuthStore;
